import { Customer, PriceHis, SaleItemState, SaleOrderAdd, SaleOrderAddItem, SaleOrderItemState } from "@/types/sales";
import { Button, DatePicker, Descriptions, DescriptionsProps, Divider, Empty, Flex, Form, FormItemProps, Input, InputNumber, Modal, ModalProps, notification, Select, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import SaleIOrderContent from "./style";
import SText from "@/styles/SText";
import { useTranslation } from "react-i18next";
import CountrySelect from "@/components/Selector/CountrySelect";
import Dict from "@/components/Dict";
import CurrencySelect from "@/components/Selector/CurrencySelect";
import { DictEnum } from "@/types/dict";
import { CreditLevelSelect, DepartmentSelect } from "@/components/Selector";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import { InventoryOrdersItem } from "@/types/purchase";
import SelectOrderType from "../../pages/Order/components/SelectOrderType";
import SelectCustomer from "../../../../components/Selector/SelectCustomer";
import useCopy from "@/hooks/useCopy";
import { DeleteOutlined, PlusCircleOutlined, ReadOutlined } from "@ant-design/icons";
import SelectPrice from "../../pages/Order/components/SelectPrice";
import { Product } from "@/types/product";
import { numFormmater } from "@/pages/Requirement/util";
import { clampDiscountValue, divided, dp, plus } from "@/utils/calc";
import dayjs from "dayjs";
import useDataAdd from "@/hooks/useDataAdd";
import api, { itemFetch, orderFetch, updateByKey, updateFinishState } from "@/apis/order";
import FileUpload, { FileUploadRef } from "@/components/FileUpload";
import { upload } from "@/utils/file/upload";
import InventoryList from "@/pages/Requirement/pages/Detail/components/InventoryList";
import { WFile } from "@/types/file";
import { getFileInfo } from "@/apis/file";
import { orderPackOrders } from "@/apis/inventoryv2";
import { packShipments } from "@/apis/packing";
import { orderFetch as shipmentOrderFetch } from "@/apis/shipment";
import { ChangeEnum, ConfirmedEnum } from "@/types/inventory";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { hasPermission } from "@/utils/user";
import { UserPermissionEnum } from "@/types/user";
import { SConModal } from "@/styles/SConModal";

const SHIPMENT_DELIVERED_STATUS = 7; // 全部送达，与物流签收时更新的发货单状态一致。


interface SaleItemAdd extends SaleOrderAddItem {
  id?: string;
  priceHis?: PriceHis;
  discountedPriceSign?: "+" | "-";
  bdiscountedPriceSign?: "+" | "-";
}

interface SaleOrderForm extends Omit<SaleOrderAdd, 'items'> {
  items: Array<SaleItemAdd>;
  remark: string;
}

interface ActualOutItem {
  productId: string;
  count: number;
}


interface ItemsOrderFormProps extends ModalProps {
  leftTitle: ReactNode;
  onClose: () => void;
  onFormSuccess: (resOrder: SaleOrderItemState, type: "ADD" | "UPDATE", keepOpen?: boolean) => void;
  editMode: "ADD" | "EDIT" | "READ";
  recordData?: SaleOrderItemState;
  fetchId?: string;
  miniStyle?: boolean;
  noModal?: boolean;
  autoHeight?: boolean;
  wmsApi?: string;
}

const ItemsOrderForm: FC<ItemsOrderFormProps> = ({ leftTitle, onClose, onFormSuccess,
  editMode, recordData, fetchId, miniStyle = false, noModal = false, autoHeight = false, wmsApi,
  ...restProps }) => {
  const { onPaste } = useCopy();

  const { t } = useTranslation();
  const { pageId } = usePage();
  const curUser = useSelector((state: RootState) => state.userReducer.curUser);
  const { add } = useDataAdd<[SaleOrderAdd], SaleOrderItemState>({ api: api.add });

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const [showList, setShowList] = useState<boolean>(false);
  const [hasChangeIds, setHasChangeIds] = useState<Array<string>>([]);
  const [updateing, setUpdateing] = useState<boolean>(false);
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [hasChange, setHasChange] = useState<boolean>(false);
  const [bomFile, setBomFile] = useState<WFile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [formRef] = Form.useForm<SaleOrderForm>();

  const fileUploadRef = useRef<FileUploadRef>(null);

  const [detailDate, setDetailDate] = useState<SaleOrderItemState | null>(null);
  const [finishCheckLoading, setFinishCheckLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [finishableOrder, setFinishableOrder] = useState<SaleOrderItemState | null>(null);

  const inventoryFetchIds = useMemo(() => (detailDate?.items ?? []).map(item => item.id), [detailDate?.items]);

  const totalSaleQuantity = useMemo(() => Number((detailDate?.items ?? []).reduce(
    (total, item) => plus(total, plus(String(item.quantity ?? 0), String(item.bquantity ?? 0))),
    '0',
  )), [detailDate?.items]);

  useEffect(() => {
    setFinishCheckLoading(false);
    setFinishableOrder(null);
    if (pageId !== PageId.F_SALE_OFFICIAL || editMode !== "READ" || !detailDate || detailDate.finishState === 1) return;

    const controller = new AbortController();
    const saleItemIds = Array.from(new Set(detailDate.items.map(item => item.id).filter(Boolean)));
    setFinishCheckLoading(true);

    const checkFinishOrder = async () => {
      let errorMessage = "统计订单实发数量失败";
      try {
        if (saleItemIds.length === 0) return;
        const saleItems = await Promise.all(saleItemIds.map(id => itemFetch(id, controller)));
        if (controller.signal.aborted) return;

        const inventoryOrderIds = new Set<string>();
        const actualOutItems = new Map<string, ActualOutItem>();
        saleItems.forEach(saleItem => {
          (saleItem.inventoryOrders ?? []).forEach(order => {
            if (![ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(order.type)) return;
            if (order.confirmState !== ConfirmedEnum.CONFIRM) return;
            if (order.id) inventoryOrderIds.add(order.id);
            (order.items ?? []).forEach(item => {
              // 同一出入库明细可能由多个销售明细返回，只累计一次。
              actualOutItems.set(item.id, {
                productId: item.productId,
                count: Number(item.count ?? 0),
              });
            });
          });
        });
        const actualOutItemList = Array.from(actualOutItems.values());
        const totalActualOutQuantity = Number(actualOutItemList.reduce(
          (total, item) => plus(total, String(item.count)),
          '0',
        ));
        if (!Number.isFinite(totalSaleQuantity)
          || !Number.isFinite(totalActualOutQuantity)
          || totalSaleQuantity !== totalActualOutQuantity) return;
        if (inventoryOrderIds.size === 0) return;

        const inventoryProductIds = new Set(actualOutItemList.map(item => item.productId).filter(Boolean));

        errorMessage = "查询关联装箱单失败";
        const packOrders = await orderPackOrders(Array.from(inventoryOrderIds), controller);
        if (controller.signal.aborted || packOrders.length === 0) return;
        const packOrderIds = packOrders.map(order => order.id);

        errorMessage = "查询关联发货单及详情失败";
        const shipments = await packShipments(packOrderIds, controller);
        if (controller.signal.aborted) return;
        const shipmentIds = Array.from(new Set(shipments.map(shipment => shipment.id).filter(Boolean)));
        if (shipmentIds.length === 0) return;
        const shipmentDetails = await Promise.all(
          shipmentIds.map(id => shipmentOrderFetch(id, controller)),
        );
        if (controller.signal.aborted) return;

        const shippedItems = shipmentDetails.flatMap(shipment =>
          (shipment.items ?? []).filter(item => inventoryProductIds.has(item.productId)),
        );
        const totalShippedQuantity = Number(shippedItems.reduce(
          (total, item) => plus(total, String(item.count ?? 0)),
          '0',
        ));
        const allShipmentsDelivered = shipmentDetails.length > 0
          && shipmentDetails.every(shipment => shipment.status === SHIPMENT_DELIVERED_STATUS);
        const canFinishOrder = Number.isFinite(totalShippedQuantity)
          && allShipmentsDelivered
          && totalShippedQuantity >= totalActualOutQuantity;
        setFinishableOrder(canFinishOrder ? detailDate : null);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(errorMessage, { orderNumber: detailDate.number, error });
      } finally {
        if (!controller.signal.aborted) {
          setFinishCheckLoading(false);
        }
      }
    };

    void checkFinishOrder();

    return () => controller.abort();
  }, [detailDate, editMode, totalSaleQuantity, pageId]);

  const getAdjustmentSignByAmount = (
    amount: string | number | undefined,
    quantity: string | number | undefined,
    itemPrice: string | number | undefined,
    adjustmentPrice: string | number | undefined,
  ): "+" | "-" => {
    const totalAmount = Number(amount || 0);
    const totalQuantity = Number(quantity || 0);
    const priceValue = Number(itemPrice || 0);
    const adjustmentValue = Number(adjustmentPrice || 0);

    if (totalQuantity <= 0 || adjustmentValue <= 0) return "-";

    const minusAmount = totalQuantity * (priceValue - adjustmentValue);
    const plusAmount = totalQuantity * (priceValue + adjustmentValue);

    return Math.abs(totalAmount - plusAmount) < Math.abs(totalAmount - minusAmount) ? "+" : "-";
  };

  const getAdjustedUnitPrice = (itemPrice: number, adjustmentPrice: number, sign: "+" | "-") => {
    return sign === "+" ? itemPrice + adjustmentPrice : itemPrice - adjustmentPrice;
  };

  const validateAdjustmentField = (name: number, field: "discountedPrice" | "bdiscountedPrice") => {
    formRef.validateFields([["items", name, field]]).catch(() => undefined);
  };

  useEffect(() => {
    if (recordData) handleDtlData(recordData);
    else if (!!fetchId) {
      setLoading(true);
      orderFetch(fetchId)
        .then(res => handleDtlData(res))
        .catch(() => { })
        .finally(() => setLoading(false))
    }
  }, [recordData, fetchId]);

  const handleDtlData = async (data: SaleOrderItemState, isCopy?: boolean) => {
    setDetailDate({ ...data });
    if (data.bomFileId) {
      try {
        let file = await getFileInfo(data.bomFileId, wmsApi);
        if (!!!file) return;
        //复制来的需要手动调用fileUploadRef添加文件组件内地新文件，否则直接设置setBomFile作为文件组件默认文件
        if (!!isCopy) fileUploadRef.current?.addLocalFiles(file)
        else setBomFile({ ...file });
      } catch (error) {
        notification.error({ message: t('bom.fileErr') })
      }
    }
  }

  useEffect(() => {
    if (detailDate) {
      formRef.setFieldsValue({
        ...detailDate,
        shippingMethod: detailDate.shippingMethod || '',
        otherNumber1: detailDate.otherNumber1 || '',
        discount: detailDate.discount || '',
        items: detailDate.items.map(item => ({
          ...item,
          discountedPriceSign: getAdjustmentSignByAmount(item.amount, item.quantity, item.price, item.discountedPrice),
          bdiscountedPriceSign: getAdjustmentSignByAmount(item.bamount, item.bquantity, item.price, item.bdiscountedPrice),
          priceHis: {
            id: item.priceHisId,
            price: item.price,
            product: item.product,
            productId: item.productId
          }
        })),
        mfgr: detailDate?.mfgr || '',
        customerModel: detailDate?.customerModel || '',
        urgencyLevel: detailDate?.urgencyLevel || '',
        specialReq: detailDate?.specialReq || '',
        remark: detailDate?.items?.[0]?.remark || '',
      })
      setSelectedCustomer(detailDate.customer)
    }
  }, [detailDate])

  const handleFinishOrder = async () => {
    if (!detailDate || loading || finishLoading || finishableOrder !== detailDate) return;

    const order = detailDate;
    setFinishLoading(true);
    try {
      const updatedOrder = await updateFinishState(order.id, 1);
      const updatedDetail: SaleOrderItemState = {
        ...order,
        finishState: updatedOrder.finishState,
      };
      notification.success({ message: t('common.saveOk'), description: order.number });
      onFormSuccess(updatedDetail, "UPDATE");
    } catch (error) {
      notification.error({ message: t('common.saveFAil'), description: order.number });
    } finally {
      setFinishLoading(false);
    }
  };

  // 判断是否可以转为正式订单
  const canToOfficial = pageId === PageId.SALE_NOT_PASS && editMode === "EDIT"
    && hasPermission(curUser, [UserPermissionEnum.SALE_TO_OFFICIAL]) && !detailDate?.requirement

  // 转为正式订单
  const onToOfficial = async () => {
    if (!detailDate) return;
    try {
      await updateByKey(detailDate.id, { key: 'preApproval', value: 1 });
      notification.success({ placement: 'top', message: t('sale.toOfficialSuccess') });
      onFormSuccess({ ...detailDate, preApproval: 1 }, "UPDATE");
    } catch (error) {
      notification.error({ placement: 'top', message: t('sale.toOfficialError'), description: detailDate.number });
    }
  };

  const TitleHeader = (
    <Flex align="baseline">
      <span style={{ display: 'inline-block', fontSize: 20, fontWeight: 600, marginTop: 10 }}>{
        noModal ? detailDate?.number :
          t("sale.orderInfo")
      }</span>
      {canToOfficial && (
        <span style={{ marginLeft: 'auto' }}>
          <SConModal title={t('common.tip')} content={t('sale.toOfficialSingleConfirm')}
            onOk={onToOfficial} disable={loading || updateing}>
            <Button type="primary" disabled={loading || updateing}>{t('sale.toOfficial')}</Button>
          </SConModal>
        </span>
      )}
      {(pageId === PageId.SALE_OFFICIAL || pageId === PageId.F_SALE_OFFICIAL) && (
        <Space>
          <Button type="primary" onClick={() => setShowList(true)} disabled={false} style={{ marginLeft: 10 }}>{t(('purchase.outOrder'))}</Button>
          {pageId === PageId.F_SALE_OFFICIAL && editMode === "READ" && detailDate?.finishState !== 1 && (
            <Button
              type="primary"
              loading={loading || finishCheckLoading || finishLoading}
              disabled={detailDate === null || finishableOrder !== detailDate}
              onClick={() => handleFinishOrder()}
            >
              {t('sale.finishOrder')}
            </Button>
          )}
        </Space>
      )}
    </Flex>
  )

  const getCustomerDesc: () => DescriptionsProps["items"] = () => [
    {
      key: "code",
      label: t("sale.code"),
      children: <SText text={detailDate?.customer?.code || ""} />,
      span: miniStyle ? 3 : 2,
    },
    {
      key: "shortName",
      label: t("sale.shortName"),
      children: <SText text={detailDate?.customer?.shortName || ""} />,
      span: miniStyle ? 3 : 1,
    },
    {
      key: "name1",
      label: t("sale.name1"),
      children: <SText text={detailDate?.customer?.name1 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "name2",
      label: t("sale.name2"),
      children: <SText text={detailDate?.customer?.name2 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "country",
      label: t("sale.country"),
      children: <CountrySelect value={detailDate?.customer?.country} readOnly />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "address",
      label: t("sale.address"),
      children: <SText text={detailDate?.customer?.address || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "zipCode",
      label: t("sale.zipCode"),
      children: <SText text={detailDate?.customer?.zipCode || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "email",
      label: t("sale.email"),
      children: <SText text={detailDate?.customer?.email || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "contact",
      label: t("sale.contact"),
      children: <SText text={detailDate?.customer?.contact || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "phone",
      label: t("sale.phone"),
      children: <SText text={detailDate?.customer?.phone || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "fax",
      label: t("sale.fax"),
      children: <SText text={detailDate?.customer?.fax || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "mobile",
      label: t("sale.mobile"),
      children: <SText text={detailDate?.customer?.mobile || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "account1",
      label: t("sale.account1"),
      children: <SText text={detailDate?.customer?.account1 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "bank1",
      label: t("sale.bank1"),
      children: <SText text={detailDate?.customer?.bank1 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "swiftCode1",
      label: t("sale.swiftCode1"),
      children: <SText text={detailDate?.customer?.swiftCode1 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "account2",
      label: t("sale.account2"),
      children: <SText text={detailDate?.customer?.account2 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "bank2",
      label: t("sale.bank2"),
      children: <SText text={detailDate?.customer?.bank2 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "swiftCode2",
      label: t("sale.swiftCode2"),
      children: <SText text={detailDate?.customer?.swiftCode2 || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "currency",
      label: t("sale.currency"),
      children: <CurrencySelect value={detailDate?.customer?.currency} readOnly />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "saleType",
      label: t("sale.saleType"),
      children: (
        <Dict type={DictEnum.SALE_TYPE} value={detailDate?.customer?.saleType} readOnly />
      ),
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "creditLevel",
      label: t("sale.creditLevel"),
      children: <CreditLevelSelect value={detailDate?.customer?.creditLevel} readOnly />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "creditLine",
      label: t("sale.creditLine"),
      children: <SText text={detailDate?.customer?.creditLine || ""} />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "depositRate",
      label: t("sale.depositRate"),
      children: <SText text={detailDate?.customer?.depositRate || ""} suffix="%" />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "tradeTerm",
      label: t("sale.tradeTerm"),
      children: (
        <Dict type={DictEnum.SALE_TRADE_TERM} value={detailDate?.customer?.tradeTerm} readOnly style={{ flex: 1 }} />
      ),
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "payMethod",
      label: t("sale.payMethod"),
      children: (
        <Dict type={DictEnum.SALE_PAY_METHOD} value={detailDate?.customer?.payMethod} readOnly style={{ flex: 1 }} />
      ),
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "taxRate",
      label: t("sale.taxRate"),
      children: <SText text={detailDate?.customer?.taxRate || ""} suffix="%" />,
      span: miniStyle ? 3 : undefined,
    },
    {
      key: "org",
      label: t("sale.org"),
      children: <DepartmentSelect readOnly value={detailDate?.customer?.orgId} />,
      span: 3,
    },
  ];

  const getProductDesc = (product: Product | undefined | null) => {
    let desc: DescriptionsProps["items"] = [];
    if (product)
      desc = [
        {
          key: "product",
          label: t("product.code"),
          children: product?.code || "",
          span: miniStyle ? 3 : undefined,
        },
        {
          key: "name",
          label: t("product.name"),
          children: product?.name || "",
          span: miniStyle ? 3 : undefined,
        },
        {
          key: "uom",
          label: t("product.uom"),
          children: product?.uom?.toLocaleUpperCase() || "",
          span: miniStyle ? 3 : undefined,
        },
        {
          key: "desc",
          label: t("common.desc"),
          children: <SText text={product?.desc || ""} style={{ width: "100%" }} />,
          span: miniStyle ? 3 : undefined,
        },
        {
          key: "brand1",
          label: t("product.brand1"),
          children: <SText text={product?.model?.toLocaleUpperCase() || ""} style={{ width: "100%" }} />,
          span: miniStyle ? 3 : undefined,
        },
        {
          key: "brand2",
          label: t("product.brand2"),
          children: <SText text={product?.t1?.toLocaleUpperCase() || ""} style={{ width: "100%" }} />,
          span: miniStyle ? 3 : undefined,
        },
        {
          key: "cbm",
          label: t("product.cbm"),
          children: product?.cbm || "",
          span: miniStyle ? 3 : undefined,
        }
      ]
    return desc
  }

  const handlePrice = (type: 'Q' | 'BQ' | 'AD' | 'BAD' | 'D' | 'BD' | 'TXR' | 'P' | 'AS' | 'BAS', value: string | number, name: number) => {

    let itemsData: Array<SaleItemAdd> = formRef.getFieldValue("items");
    let handleData = itemsData[name];
    if (!handleData) return;

    const currentDiscountedPriceSign = type === 'AS'
      ? value as "+" | "-"
      : handleData.discountedPriceSign === "+" ? "+" : "-";
    const currentBdiscountedPriceSign = type === 'BAS'
      ? value as "+" | "-"
      : handleData.bdiscountedPriceSign === "+" ? "+" : "-";
    let PRICE = Number(handleData?.priceHis?.price || '0');
    let Q = Number(handleData?.quantity || '0') || 0;
    let BQ = Number(handleData?.bquantity || '0') || 0;
    let AD = Number(handleData?.discountedPrice || '0') || 0;
    let BAD = Number(handleData.bdiscountedPrice || '0') || 0;
    let D = Number(handleData?.discount ?? '0') || 0;
    let BD = Number(handleData.bdiscount || '0') || 0;
    let val = Number(value) || 0;

    if (type !== "TXR") setHasChangeIds(prev => [...prev, handleData.id ?? '']);
    if (type === 'TXR') {
      setHasChange(true)
      let allRealAmountTxr = '0';
      itemsData.forEach(item => {
        allRealAmountTxr = plus(plus(item.realAmount, item.brealAmount), allRealAmountTxr)
      })
      formRef.setFieldValue('totalRealAmount', allRealAmountTxr);
      return
    }

    if (type === 'P') {
      PRICE = Number(value);
      let quantity = Number(handleData.quantity || '')
      let bquantity = Number(handleData.bquantity || '')
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      const bunitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      handleData.amount = (quantity * unitPrice || '0') + ''
      handleData.realAmount = (quantity * unitPrice - D || '0') + ''
      handleData.bamount = (bquantity * bunitPrice || '0') + ''
      handleData.brealAmount = (bquantity * bunitPrice - BD || '0') + ''
    }

    if (type === 'Q') { //数量
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      handleData.amount = (val * unitPrice || '0') + ''
      handleData.realAmount = (val * unitPrice - D || '0') + ''
    }
    if (type === 'BQ') { //备品数量
      const unitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      handleData.bamount = (val * unitPrice || '0') + ''
      handleData.brealAmount = (val * unitPrice - BD || '0') + ''
    }
    if (type === 'AD' || type === 'AS') { //调整价格
      handleData.discountedPriceSign = currentDiscountedPriceSign;
      const adjustment = type === 'AD' ? val : AD;
      const unitPrice = getAdjustedUnitPrice(PRICE, adjustment, currentDiscountedPriceSign);
      handleData.amount = (Q * unitPrice || '0') + ''
      handleData.realAmount = (Q * unitPrice - D || '0') + ''
    }
    if (type === 'BAD' || type === 'BAS') { //备品调整价格
      handleData.bdiscountedPriceSign = currentBdiscountedPriceSign;
      const adjustment = type === 'BAD' ? val : BAD;
      const unitPrice = getAdjustedUnitPrice(PRICE, adjustment, currentBdiscountedPriceSign);
      handleData.bamount = (BQ * unitPrice || '0') + ''
      handleData.brealAmount = (BQ * unitPrice - BD || '0') + ''
    }
    if (type === 'D') { //折扣
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      handleData.realAmount = (Q * unitPrice - val || '0') + ''
    }
    if (type === 'BD') { //备品折扣
      const unitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      handleData.brealAmount = (BQ * unitPrice - val || '0') + ''
    }

    let amount = Number(handleData.amount || 0)
    let bAmount = Number(handleData.bamount || 0)
    const discountValue = Number(handleData.discount || 0);
    const nextDiscount = clampDiscountValue(discountValue, amount);
    if (nextDiscount !== discountValue) {
      handleData.discount = `${nextDiscount}`
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      handleData.realAmount = (Q * unitPrice - nextDiscount || '0') + ''
    }
    const bdiscountValue = Number(handleData.bdiscount || 0);
    const nextBdiscount = clampDiscountValue(bdiscountValue, bAmount);
    if (nextBdiscount !== bdiscountValue) {
      handleData.bdiscount = `${nextBdiscount}`
      const unitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      handleData.brealAmount = (BQ * unitPrice - nextBdiscount || '0') + ''
    }
    handleData.totalRealAmount = dp(plus(handleData.realAmount, handleData.brealAmount))

    let newAllItems = itemsData.map((item, index) => index === name ? handleData : item)
    let allRealAmount = '0';

    newAllItems.map(item => {
      allRealAmount = plus(plus(item.realAmount, item.brealAmount), allRealAmount)
    })
    formRef.setFieldValue('items', newAllItems);

    formRef.setFieldValue('totalRealAmount', allRealAmount);
  }

  const getItemPriceInputs: (name: number, itemsData: Array<SaleItemAdd>) => ReactNode = (name, itemsData) => {
    const itemData = itemsData[name];
    const price = itemData?.priceHis?.price ?? '';
    const product = itemData?.priceHis?.product;
    const discountedPriceSign = itemData?.discountedPriceSign === "+" ? "+" : "-";
    const bdiscountedPriceSign = itemData?.bdiscountedPriceSign === "+" ? "+" : "-";
    let inlineFileds: Array<Array<FormItemProps<SaleItemAdd>>> = [
      [
        {
          label: <SText text={t("sale.quantity")} style={{ width: 80 }} />,
          name: "quantity",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0}
              precision={0}
              placeholder='0'
              disabled={editMode === "READ"}
              onChange={val => handlePrice('Q', val + '', name)}
              style={{ width: 80 }} controls={false}
              onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
            />
          ),
        },
        {
          label: <SText text={t("sale.price")} style={{ width: 80 }} />,
          layout: 'vertical',
          children: <InputNumber changeOnWheel placeholder='0' value={price} disabled style={{ width: 80 }} controls={false} />
        },
        {
          label: <SText text={t("sale.revisePrice")} style={{ width: 60 }} />,
          name: "discountedPrice",
          layout: 'vertical',
          className: "silent-error-form-item",
          help: false,
          rules: [{
            validator: (_, value) => {
              const currentItem = formRef.getFieldValue(["items", name]) as SaleItemAdd | undefined;
              const currentSign = currentItem?.discountedPriceSign === "+" ? "+" : "-";
              const currentPrice = currentItem?.priceHis?.price ?? price;
              if (currentSign === "+" || Number(value || 0) <= Number(currentPrice || 0)) return Promise.resolve();
              return Promise.reject(new Error(t("sale.priceMax", { num: currentPrice || 0 })));
            }
          }],
          children: (
            <InputNumber changeOnWheel
              min={'0'}
              max={discountedPriceSign === "-" ? price : undefined}
              placeholder='0'
              disabled={!!!product || editMode === "READ"}
              onChange={val => handlePrice('AD', val + '', name)}
              style={{ width: 60 }} controls={false}
              onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
            />
          ),
        },
        {
          label: <SText text={t("sale.totalPrice")} style={{ width: 90 }} />,
          name: "amount",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0}
              precision={0}
              placeholder='0'
              disabled style={{ width: 90 }} controls={false} formatter={val => numFormmater(val || 0)}
            />
          ),
        },
        {
          label: <SText text={t("sale.discount")} style={{ width: 90 }} />,
          name: "discount",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0} max={Math.max(Number(itemData?.amount || 0), 0)}
              placeholder='0'
              disabled={!!!product || editMode === "READ"}
              onChange={val => handlePrice('D', val + '', name)}
              style={{ width: 90 }} controls={false}
              onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
            />
          ),
        },
        {
          label: <SText text={t("sale.sPrice")} style={{ width: 90 }} />,
          name: "realAmount",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0}
              precision={0}
              placeholder='0' formatter={val => numFormmater(val || 0)}
              disabled style={{ width: 90 }} controls={false}
            />
          ),
        },
      ],
      [
        {
          label: <SText text={t("sale.send")} style={{ width: 80 }} />,
          name: "bquantity",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0}
              precision={0}
              placeholder='0'
              disabled={!!!product || editMode === "READ"}
              onChange={val => handlePrice('BQ', val + '', name)}
              style={{ width: 80 }} controls={false}
              onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
            />
          ),
        },
        {
          label: <SText text={t("sale.price")} style={{ width: 80 }} />,
          layout: 'vertical',
          children: <InputNumber changeOnWheel placeholder='0' value={price} disabled style={{ width: 80 }} controls={false} />
        },
        {
          label: <SText text={t("sale.revisePrice")} style={{ width: 60 }} />,
          name: "bdiscountedPrice",
          layout: 'vertical',
          className: "silent-error-form-item",
          help: false,
          rules: [{
            validator: (_, value) => {
              const currentItem = formRef.getFieldValue(["items", name]) as SaleItemAdd | undefined;
              const currentSign = currentItem?.bdiscountedPriceSign === "+" ? "+" : "-";
              const currentPrice = currentItem?.priceHis?.price ?? price;
              if (currentSign === "+" || Number(value || 0) <= Number(currentPrice || 0)) return Promise.resolve();
              return Promise.reject(new Error(t("sale.priceMax", { num: currentPrice || 0 })));
            }
          }],
          children: (
            <InputNumber changeOnWheel
              min={'0'} max={bdiscountedPriceSign === "-" ? price : undefined}
              placeholder='0'
              disabled={!!!product || editMode === "READ"}
              onChange={val => handlePrice('BAD', val + '', name)}
              style={{ width: 60 }} controls={false}
              onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
            />
          ),
        },
        {
          label: <SText text={t("sale.totalPrice")} style={{ width: 90 }} />,
          name: "bamount",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0}
              precision={0}
              placeholder='0' formatter={val => numFormmater(val || 0)}
              disabled style={{ width: 90 }} controls={false}
            />
          ),
        },
        {
          label: <SText text={t("sale.discount")} style={{ width: 90 }} />,
          name: "bdiscount",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0} max={Math.max(Number(itemData?.bamount || 0), 0)}
              placeholder='0'
              disabled={!!!product || editMode === "READ"}
              onChange={val => handlePrice('BD', val + '', name)}
              style={{ width: 90 }} controls={false}
              onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
            />
          ),
        },
        {
          label: <SText text={t("sale.sendPrice")} style={{ width: 90 }} />,
          name: "brealAmount",
          layout: 'vertical',
          children: (
            <InputNumber changeOnWheel
              min={0}
              precision={0}
              placeholder='0' formatter={val => numFormmater(val || 0)}
              disabled style={{ width: 90 }} controls={false}
            />
          ),
        },
      ]
    ]
    return (
      <>
        <Flex>
          {inlineFileds[0].map(sitem => {
            if (sitem.name === 'quantity') {
              return <>
                <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>X</div></Flex>
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>(</div></Flex>
              </>
            } else if (sitem.name === 'discountedPrice') {
              return <>
                <Flex align="center">
                  <Select
                    value={discountedPriceSign}
                    options={[
                      { value: "-", label: "-" },
                      { value: "+", label: "+" },
                    ]}
                    style={{ width: 60, marginTop: 26, marginRight: 10 }}
                    disabled={!!!product || editMode === "READ"}
                    onChange={(val) => {
                      handlePrice('AS', val, name);
                      validateAdjustmentField(name, "discountedPrice");
                    }}
                  />
                </Flex>
                <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>)</div></Flex>
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
              </>
            }
            else if (sitem.name === 'discount') {
              return <>
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>-</div></Flex>
                <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
              </>
            } else return <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
          })}
        </Flex>
        <Flex>
          <div style={{ width: 60, marginLeft: 640, textAlign: 'center', fontSize: 20 }}>+</div>
        </Flex>
        <Flex>
          {inlineFileds[1].map(sitem => {
            if (sitem.name === 'bquantity') {
              return <>
                <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>X</div></Flex>
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>(</div></Flex>
              </>
            } else if (sitem.name === 'bdiscountedPrice') {
              return <>
                <Flex align="center">
                  <Select
                    value={bdiscountedPriceSign}
                    options={[
                      { value: "-", label: "-" },
                      { value: "+", label: "+" },
                    ]}
                    style={{ width: 60, marginTop: 26, marginRight: 10 }}
                    disabled={!!!product || editMode === "READ"}
                    onChange={(val) => {
                      handlePrice('BAS', val, name);
                      validateAdjustmentField(name, "bdiscountedPrice");
                    }}
                  />
                </Flex>
                <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>)</div></Flex>
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
              </>
            }
            else if (sitem.name === 'bdiscount') {
              return <>
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>-</div></Flex>
                <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
                <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
              </>
            } else return <Form.Item {...sitem} name={!!sitem.name ? [name, sitem.name] : undefined as any} />
          })}
        </Flex>
        <Flex>
          <div style={{ width: 60, marginLeft: 640, textAlign: 'center', fontSize: 20 }}>=</div>
        </Flex>
        <Flex>
          <Form.Item style={{ marginLeft: 628, marginBottom: 0, width: 100 }}>
            <InputNumber changeOnWheel
              precision={0}
              formatter={val => numFormmater(Number(val || 0))}
              disabled style={{ width: 100 }}
              value={itemData.totalRealAmount}
            />
          </Form.Item>
        </Flex>
      </>
    )
  }

  const handleDelBom = async (id: string) => {
    if (id && detailDate && id === detailDate.bomFileId) {
      setLoading(true);
      try {
        await updateByKey(detailDate.id, { key: "bomFileId", value: '' });
        onFormSuccess({ ...detailDate, bomFileId: '' }, "UPDATE", true);
        notification.success({ message: t('common.rmSuccess') })
      } catch (error) { }
      setLoading(false);
    }
  }

  const handleSubmit = async (values: SaleOrderForm) => {

    let realAmount = divided(values.totalRealAmount, plus('1', divided(values.taxRate ?? '0', '100'))) //未税总价
    values.totalAmount = dp(realAmount)
    values.realAmount = realAmount

    if (editMode === "ADD") {
      if (addLoading) return;
      setAddLoading(true);
      let saleAddInfo: SaleOrderAdd = {
        ...values,
        productId: values.items[0].priceHis?.productId ?? '',
        items: values.items.filter(item => !!item.priceHisId).map((item, index) => {
          let tempData = {
            ...item,
            remark: index === 0 ? values.remark : '',
          };
          delete tempData.priceHis
          delete tempData.id
          delete tempData.discountedPriceSign
          delete tempData.bdiscountedPriceSign

          return tempData
        }),
      }
      if (saleAddInfo.items.length === 0) return notification.error({
        message: t('product.selProduct')
      })
      let files = fileUploadRef.current?.getFiles()
      if (files && files.newFiles && files.newFiles.length > 0) {
        let rcFile = files.newFiles[0].originFileObj;
        if (rcFile) {
          let fileRes = await upload({ name: rcFile.name, size: rcFile.size, file: rcFile })
          saleAddInfo.bomFileId = fileRes?.id
        } else {
          //没有rcFile证明是手动addLocalFiles进去的，直接取值uid
          saleAddInfo.bomFileId = files.newFiles[0].uid;
        }
      }
      await add(saleAddInfo)
        .then(res => {
          notification.success({
            message: t('common.addSuccess'),
            description: res.number
          })
          onFormSuccess({
            ...res,
            customer: selectedCustomer!
          }, "ADD")
        })
        .catch(() => {
          notification.error({ message: t('common.addError') })
        })
        .finally(() => {
          setAddLoading(false)
          setHasChangeIds([])
        })

    }
    if (detailDate && editMode === "EDIT") {
      if (updateing) return;
      setUpdateing(true);
      let fileData = fileUploadRef.current?.getFiles();
      if (fileData?.delFiles && fileData.delFiles.length > 0) {
        // 没有删除接口
      }
      if (fileData?.newFiles && fileData.newFiles.length > 0) {
        let rcFile = fileData.newFiles[0].originFileObj;
        if (rcFile) {
          let fileRes = await upload({ name: rcFile.name, size: rcFile.size, file: rcFile });
          await updateByKey(detailDate.id, { key: "bomFileId", value: fileRes?.id ?? '' })
        }
      }
      let addItems: Array<SaleOrderAddItem> = values.items.filter(item => !!!item.id)
        .map(item => {
          let tempData = { ...item };
          delete tempData.priceHis
          delete tempData.id
          delete tempData.discountedPriceSign
          delete tempData.bdiscountedPriceSign

          return tempData
        })

      let updateItems = values.items.filter(item => !!item.id && hasChangeIds.includes(item.id))
        .map(item => {
          let tempData = { ...item };
          delete tempData.priceHis
          delete tempData.discountedPriceSign
          delete tempData.bdiscountedPriceSign

          return tempData
        })

      let orderUpdateInfo: SaleOrderAdd = {
        ...detailDate,
        otherNumber1: detailDate?.otherNumber1 ?? '',
        discount: detailDate?.discount ?? '',
        ...values,
        items: []
      }
      try {
        let newItems: Array<SaleItemState> = detailDate.items.filter(item => !hasChangeIds.includes(item.id));
        if (addItems.length > 0) {
          for (let i = 0; i < addItems.length; i++) {
            let item = await api.itemAdd(detailDate.id, addItems[i])
            newItems.push(item)
          }
        }
        if (updateItems.length > 0) {
          for (let i = 0; i < updateItems.length; i++) {
            let item = await api.saleItemUpdate(updateItems[i].id ?? "", {
              ...updateItems[i],
              remark: updateItems[i].id === detailDate.items[0].id ? values.remark : ''
            })
            newItems.push(item)

          }
        }
        let resOrder: SaleOrderItemState | null = null;
        if (hasChange || hasChangeIds.length > 0) {
          resOrder = await api.update(detailDate.id, orderUpdateInfo);
        } else {
          setUpdateing(false);
          onClose();
          return
        }
        if (resOrder) {
          notification.success({
            message: t('common.saveOk'),
          })
          setHasChange(false);
          setHasChangeIds([]);
          setUpdateing(false);
          onFormSuccess({
            ...resOrder,
            items: [...newItems]
          }, "UPDATE");
        } else {
          setAddLoading(false)
          notification.error({
            message: t('common.saveFAil'),
          })
        }

      } catch (error) {
        setAddLoading(false)
        notification.error({
          message: t('common.saveFAil'),
        })
      }
    }
  };

  const MainContent = (
    <>
      <SaleIOrderContent ref={scrollContainerRef} style={autoHeight ? { height: "100%", overflow: "unset" } : undefined}>
        <Form<SaleOrderForm> form={formRef} onFinish={handleSubmit} onChange={() => setHasChange(true)} disabled={editMode === "READ"}>
          {/* 订单信息字段 */}
          <Form.Item<SaleOrderForm> noStyle>
            {TitleHeader}
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.orderType")} name="type"
            required={true}
            rules={[{ required: true, message: t("sale.plOrderType") }]}
          >
            <SelectOrderType readOnly={editMode !== "ADD"} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.customerInfo")} name="customerId"
            required={true}
            rules={[{ required: true, message: t("sale.plCustomer") }]}
          >
            <SelectCustomer
              disabled={editMode !== "ADD"}
              defaultList={selectedCustomer ? [selectedCustomer] : []}
              onChangeCustomer={(e) => {
                setSelectedCustomer(e)
                formRef.setFieldsValue({
                  receiver: e?.shortName || e?.name1 || e?.name2 || e?.code,
                  receiverAddress: e?.address || "",
                  receiverContact: e?.contact || "",
                  receiverMobile: e?.mobile || "",
                  country: e?.country,
                  taxRate: e?.taxRate,
                  // customerModel: '',
                  // mfgr: '',
                  currency: e.currency,
                  commissionRate: e.commissionRate,
                  items: [],
                  totalAmount: '',
                  totalRealAmount: '',
                  realAmount: '',
                  receiverFax: e.fax ?? '',
                  receiverPhone: e.phone ?? '',
                });
                setTimeout(() => formRef.setFieldValue("items", [{ priceHisId: '', discountedPriceSign: '-', bdiscountedPriceSign: '-' }]))
              }}
            />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.innerNumber")} name="innerNumber">
            <Input showCount maxLength={50} placeholder={t("sale.plInnerNumber")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.invoiceNumber")} name="invoiceNumber">
            <Input showCount maxLength={50} placeholder={t("sale.invoiceNumber")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.receivingUnit")} name="receiver">
            <Input showCount maxLength={100} placeholder={t("sale.plReceivingUnit")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.receivingContact")} name="receiverContact">
            <Input showCount maxLength={50} placeholder={t("sale.plReceivingContact")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.receivingContactMobile")} name="receiverMobile">
            <Input showCount maxLength={50} placeholder={t("sale.plReceivingContactMobile")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.receivingAddress")} name="receiverAddress">
            <Input showCount maxLength={500} placeholder={t("sale.plReceivingAddress")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.receiverFax")} name="receiverFax">
            <Input placeholder={t("sale.plFax")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.receiverPhone")} name="receiverPhone">
            <Input showCount maxLength={50} placeholder={t("sale.plPhone")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.country")} name="country">
            <CountrySelect readOnly={editMode === "READ"} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.taxRate")} name="taxRate">
            <InputNumber changeOnWheel
              max={100}
              min={0}
              style={{ width: "100%" }}
              placeholder={t("sale.plTaxRate")}
              formatter={(value) => `${value ? value + "%" : ""}`}
              disabled={editMode === "READ"}
              onChange={val => handlePrice('TXR', val || 0, 0)}
            />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.shippingMethod")} name="shippingMethod">
            <Dict type={DictEnum.SALE_SHIPPING_METHOD} readOnly={editMode === "READ"} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.currency")} name="currency"
            required
            rules={[{ required: true, message: t("components.plCurrency") }]}
          >
            <CurrencySelect readOnly={editMode === "READ"} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.commissionRate") + " (%)"} name="commissionRate">
            <InputNumber changeOnWheel
              max={100}
              min={0}
              style={{ width: "100%" }}
              placeholder={t("sale.plDictCommissionRate")}
              formatter={(value) => `${value ? value + "%" : ""}`}
              disabled={editMode === "READ"}
            />
          </Form.Item>
          {/* 终端客户信息 */}
          {
            editMode !== "ADD" && detailDate?.customer && (
              <>
                <Form.Item noStyle>
                  <Typography.Title level={4}>{t("sale.customerInfo")}</Typography.Title>
                </Form.Item>
                <Form.Item noStyle>
                  <Descriptions items={getCustomerDesc()} />
                </Form.Item>
              </>
            )
          }
          {/* 商品items */}
          <Form.Item<SaleOrderForm> noStyle><Typography.Title level={5}>{t("sale.orderProduct")}</Typography.Title></Form.Item>
          <Form.List name="items" initialValue={[{ priceHisId: '', discountedPriceSign: '-', bdiscountedPriceSign: '-' }]} >
            {(fields, { add, remove }) => (
              <div className="list-wrap">
                {fields.map((field) => {
                  let itemsData: Array<SaleItemAdd> = formRef.getFieldValue("items") || []
                  return (
                    <div className="list-item"
                      key={field.key}
                      style={editMode === "READ" ? { border: "none" } : undefined}
                    >
                      <Form.Item label={t("sale.product")} name={[field.name, "priceHisId"]}>
                        <Flex gap={4}>
                          <div style={{ flex: 1 }}>
                            <SelectPrice value={itemsData?.[field.name]?.priceHis || undefined}
                              customerId={selectedCustomer?.id ?? ''}
                              disabled={editMode === "READ"}
                              defaultList={itemsData?.[field.name]?.priceHis ? [itemsData[field.name].priceHis as PriceHis] : undefined}
                              onChange={(val) => {
                                field.name === 0 && formRef.setFieldsValue({
                                  mfgr: val?.mfgr ?? '',
                                  customerModel: val?.customerModel ?? ''
                                });
                                formRef.setFieldValue("items", itemsData.map((item, index) => ({
                                  ...item,
                                  priceHisId: val.id,
                                  productName: '',
                                  price: val.price,
                                  priceHis: field.name === index ? val : item.priceHis
                                })))
                                handlePrice("P", val.price ?? '0', field.name)
                                validateAdjustmentField(field.name, "discountedPrice");
                                validateAdjustmentField(field.name, "bdiscountedPrice");
                              }}
                            />
                          </div>
                          {/* {editMode === "EDIT" && !!!detailDate?.productId && field.name === fields.length - 1 && <Button icon={<PlusCircleOutlined />} onClick={() => {
                            add({
                              id: '',
                              priceHisId: '',
                              price: '',
                              productName: '',
                              quantity: 0,
                              discountedPrice: '',
                              discount: '',
                              bquantity: 0,
                              bdiscountedPrice: '',
                              bamount: '',
                              bdiscount: '',
                              brealAmount: '',
                              amount: '',
                              realAmount: '',
                              totalRealAmount: '',
                            });
                            setTimeout(() => {
                              scrollContainerRef.current && scrollContainerRef.current.scrollTo({
                                top: scrollContainerRef.current.scrollHeight,
                                behavior: "smooth"
                              });
                            }, 100);
                          }} />}
                          {editMode === "EDIT" && !!!detailDate?.productId && fields.length > 1 && false && <Button icon={<DeleteOutlined />} danger onClick={() => remove(field.name)} />} */}
                        </Flex>
                      </Form.Item>
                      <Form.Item noStyle>
                        <Descriptions items={getProductDesc(itemsData[field.name]?.priceHis?.product)} />
                      </Form.Item>
                      {getItemPriceInputs(field.name, itemsData)}
                    </div>
                  )
                }
                )}
              </div>
            )}
          </Form.List>
          <Form.Item<SaleOrderForm> label={t("sale.orderTotal")} name="totalRealAmount">
            <InputNumber changeOnWheel formatter={val => numFormmater(Number(val || 0))} disabled style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.mfgr")} name="mfgr">
            <Input showCount maxLength={50} placeholder={t("sale.plMfgr")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.customerModel")} name="customerModel">
            <Input showCount maxLength={50} placeholder={t("sale.plCustomerModel")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.reqDeliDate")} name="reqDeliDate"
            getValueProps={(value) => ({ value: value && dayjs(Number(value)) })}
            normalize={(value) => value && dayjs(value).valueOf()}
          >
            <DatePicker style={{ width: "100%" }} disabled={editMode === "READ"} onChange={() => setHasChange(true)} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.urgencyLevel")} name="urgencyLevel">
            <Input showCount maxLength={50} placeholder={t("sale.plUrgencyLevel")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.specialReq")} name="specialReq">
            <Input showCount maxLength={50} placeholder={t("sale.plSpecialReq")} />
          </Form.Item>
          <Form.Item<SaleOrderForm> label={t("sale.reRemark")} name="remark">
            <Input.TextArea
              showCount maxLength={3000} autoSize={{ maxRows: 8, minRows: 2 }}
              placeholder={t("sale.plReRemark")}
              onChange={() => {
                if (detailDate) {
                  if (hasChangeIds.includes(detailDate.items[0].id)) return
                  else setHasChangeIds(prev => [...prev, detailDate.items[0].id])
                }
              }}
            />
          </Form.Item>
          <Form.Item layout="vertical" label={t('bom.uploadBom')}>
            <FileUpload ref={fileUploadRef} labelText={t('bom.uploadBom')}
              files={bomFile ? [bomFile] : undefined}
              accept=".xlsx,.xls"
              maxCount={1}
              readOnly={editMode === "READ"}
              onDelFile={handleDelBom}
            />
          </Form.Item>
        </Form>
      </SaleIOrderContent>
      {!!showList && (
        <InventoryList
          fetchId={inventoryFetchIds}
          fetchType="SALE"
          number={detailDate?.number ?? ""}
          onClose={() => setShowList(false)}
        />
      )}
    </>
  )

  return (
    noModal ? MainContent
      : <Modal open width={850} onCancel={onClose} onOk={formRef.submit}
        title={
          <Flex justify="space-between" align="center">
            {leftTitle}
            <Flex align='center' gap={16} style={{ paddingRight: 44 }}>
              {editMode === "ADD" && (<Tooltip title={t("common.paste")}>
                <Button onClick={() => {
                  onPaste().then(res => {
                    let pasteData: SaleOrderItemState = JSON.parse(res);
                    handleDtlData(pasteData, true);
                    // setDetailDate({
                    //   ...pasteData,
                    // })
                  }).catch(() => {
                    notification.error({ placement: 'top', message: t("common.pasteError") })
                  })
                }} icon={<ReadOutlined />} type="text" />
              </Tooltip>)}
            </Flex>
          </Flex>
        }
        footer={editMode === "READ" ? null : undefined}
        okButtonProps={{
          loading: updateing || addLoading
        }}
        {...restProps}>
        <Spin spinning={loading}>
          {MainContent}
        </Spin>
      </Modal>
  )
};

export default ItemsOrderForm;
